import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../lib/api.js';
import TopBar from '../components/TopBar.jsx';
import { ShoppingCart, FileSignature, Pause, Play, AlertTriangle, TrendingUp, Users, Wallet, RotateCcw, BadgeCheck } from 'lucide-react';
import toast from 'react-hot-toast';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

const LEAD_TYPES = [
  { v: 'final_expense', label: 'Final Expense' },
  { v: 'medicare', label: 'Medicare' },
  { v: 'mortgage', label: 'Mortgage' },
  { v: 'auto_insurance', label: 'Auto Insurance' },
  { v: 'health_insurance', label: 'Health Insurance' },
  { v: 'life_insurance', label: 'Life Insurance' },
  { v: 'aca', label: 'ACA' },
  { v: 'aged', label: 'Aged Leads' },
  { v: 'live_transfer', label: 'Live Transfer' },
  { v: 'custom', label: 'Custom' },
];

const PRODUCT_TYPES = [
  { v: 'final_expense', label: 'Final Expense' },
  { v: 'term', label: 'Term' },
  { v: 'whole_life', label: 'Whole Life' },
  { v: 'iul', label: 'IUL' },
  { v: 'mortgage_protection', label: 'Mortgage Protection' },
  { v: 'annuity', label: 'Annuity' },
  { v: 'other', label: 'Other' },
];

export default function LeadOrders() {
  const qc = useQueryClient();
  const me = JSON.parse(localStorage.getItem('user') || '{}');
  const isManager = ['admin', 'superadmin'].includes(me.role);

  const { data: mineData } = useQuery({
    queryKey: ['lead-orders-mine'],
    queryFn: () => apiGet('/lead-orders/mine'),
  });
  const { data: clauseData } = useQuery({
    queryKey: ['lead-orders-clause'],
    queryFn: () => apiGet('/lead-orders/clause'),
  });
  const { data: allData } = useQuery({
    queryKey: ['lead-orders-all'],
    queryFn: () => apiGet('/lead-orders'),
    enabled: isManager,
  });
  const { data: statsData } = useQuery({
    queryKey: ['lead-orders-stats'],
    queryFn: () => apiGet('/lead-orders/stats/summary'),
    enabled: isManager,
  });

  const { data: walletData } = useQuery({
    queryKey: ['credits-me'],
    queryFn: () => apiGet('/credits/me'),
  });
  const { data: salesData } = useQuery({
    queryKey: ['sales-mine'],
    queryFn: () => apiGet('/sales/mine'),
  });

  const [showOrderForm, setShowOrderForm] = useState(false);
  const [showSaleForm, setShowSaleForm] = useState(false);
  const [reorderFrom, setReorderFrom] = useState(null);

  async function pauseOrder(id) {
    try {
      await apiPost(`/lead-orders/${id}/pause`, {});
      toast.success('Order paused');
      qc.invalidateQueries({ queryKey: ['lead-orders-mine'] });
      qc.invalidateQueries({ queryKey: ['lead-orders-all'] });
    } catch (e) { toast.error(e.message); }
  }
  async function resumeOrder(id) {
    try {
      await apiPost(`/lead-orders/${id}/resume`, {});
      toast.success('Order resumed');
      qc.invalidateQueries({ queryKey: ['lead-orders-mine'] });
      qc.invalidateQueries({ queryKey: ['lead-orders-all'] });
    } catch (e) { toast.error(e.message); }
  }

  function refreshOrders() {
    qc.invalidateQueries({ queryKey: ['lead-orders-mine'] });
    qc.invalidateQueries({ queryKey: ['lead-orders-all'] });
    qc.invalidateQueries({ queryKey: ['credits-me'] });
  }

  const myOrders = mineData?.orders || [];
  const pausedForFunds = myOrders.filter(
    (o) => o.status === 'paused' && /insufficient/i.test(o.pausedReason || '')
  );
  const allOrders = allData?.orders || [];
  const mySales = salesData?.sales || [];
  const stats = statsData;

  return (
    <div className="space-y-4">
      <TopBar
        title="Lead Orders"
        subtitle="Order weekly leads · Spend-share distribution · No refund / no cancel clause"
        right={
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setShowSaleForm((v) => !v); setShowOrderForm(false); }}
              className="px-3 py-1.5 rounded-md border border-green-500/40 text-green-200 text-sm font-medium hover:border-green-400"
            >
              {showSaleForm ? 'Cancel' : 'Submit a Sale'}
            </button>
            <button
              onClick={() => { setShowOrderForm((v) => !v); setShowSaleForm(false); }}
              className="px-3 py-1.5 rounded-md bg-linear-to-r from-civix-purple to-civix-cyan text-sm font-medium hover:opacity-90"
            >
              {showOrderForm ? 'Cancel' : '+ Place Order'}
            </button>
          </div>
        }
      />

      {/* Hidden from sidebar nav until VITE_ENABLE_LEAD_ORDERS=true. The page
          stays mounted so direct links continue to work — this banner warns
          visitors who reach the URL that the feature isn't ready yet. */}
      <div className="panel p-3 border border-yellow-500/30 bg-yellow-500/5 flex items-start gap-2 text-sm">
        <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 shrink-0" />
        <div className="text-xs text-white/70">
          <span className="font-medium text-yellow-200">Under construction.</span>{' '}
          This feature is hidden from navigation and not ready for production use.
        </div>
      </div>

      {/* Wallet. Orders reserve quota but debit nothing up front — each
          DELIVERED lead debits this balance. If it runs dry, the order
          soft-pauses rather than handing out leads that were never paid for. */}
      <div className="panel p-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Wallet className="w-4 h-4 text-civix-cyan" />
            <div>
              <div className="text-xs text-white/50">Lead Wallet Balance</div>
              <div className="text-2xl font-semibold tabular-nums">
                ${(walletData?.balance ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
          <div className="text-xs text-white/50 space-y-0.5 text-right">
            <div>Lifetime added: <span className="tabular-nums text-white/70">${(walletData?.lifetimeTopUp ?? 0).toLocaleString()}</span></div>
            <div>Lifetime spent: <span className="tabular-nums text-white/70">${(walletData?.lifetimeSpent ?? 0).toLocaleString()}</span></div>
            <div className="text-white/40">Each delivered lead debits this balance.</div>
          </div>
        </div>
        {pausedForFunds.length > 0 && (
          <div className="mt-3 border border-yellow-500/30 bg-yellow-500/5 rounded-md p-2 text-xs text-yellow-200 flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              {pausedForFunds.length === 1 ? 'An order is' : `${pausedForFunds.length} orders are`} paused for
              insufficient funds. Add money to your wallet, then resume the order to restart delivery.
            </span>
          </div>
        )}
      </div>

      {showSaleForm && (
        <SubmitSaleForm
          onDone={() => {
            setShowSaleForm(false);
            qc.invalidateQueries({ queryKey: ['sales-mine'] });
          }}
        />
      )}

      {reorderFrom && (
        <ReorderForm
          order={reorderFrom}
          clauseText={clauseData?.text || ''}
          clauseVersion={clauseData?.version || ''}
          onClose={() => setReorderFrom(null)}
          onDone={() => { setReorderFrom(null); refreshOrders(); }}
        />
      )}

      {showOrderForm && (
        <NewOrderForm
          clauseText={clauseData?.text || ''}
          clauseVersion={clauseData?.version || ''}
          onDone={() => {
            setShowOrderForm(false);
            qc.invalidateQueries({ queryKey: ['lead-orders-mine'] });
            qc.invalidateQueries({ queryKey: ['lead-orders-all'] });
          }}
        />
      )}

      {/* My Orders */}
      <div className="panel p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm text-white/70">
          <ShoppingCart className="w-4 h-4" /> My Orders
        </div>
        {myOrders.length === 0 ? (
          <div className="text-sm text-white/50 py-8 text-center">
            No orders yet. Place your first order to start receiving leads.
          </div>
        ) : (
          <div className="space-y-2">
            {myOrders.map((o) => (
              <OrderRow key={o._id} order={o} onPause={pauseOrder} onResume={resumeOrder} onReorder={setReorderFrom} />
            ))}
          </div>
        )}
      </div>

      {/* My Sales. Submitting a sale is what protects a closed lead from the
          second-chance pool, so the list doubles as proof of protection. */}
      <div className="panel p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm text-white/70">
          <BadgeCheck className="w-4 h-4 text-green-400" /> My Sales ({mySales.length})
        </div>
        {mySales.length === 0 ? (
          <div className="text-sm text-white/50 py-6 text-center">
            No sales submitted yet. Submitting a sale locks that lead out of the second-chance pool.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-white/50 border-b border-white/10">
                <tr>
                  <th className="text-left py-2 font-normal">Client</th>
                  <th className="text-left py-2 font-normal">Carrier</th>
                  <th className="text-left py-2 font-normal">Product</th>
                  <th className="text-right py-2 font-normal">Annual Premium</th>
                  <th className="text-left py-2 font-normal">Policy #</th>
                  <th className="text-left py-2 font-normal">Application</th>
                  <th className="text-left py-2 font-normal">Status</th>
                </tr>
              </thead>
              <tbody>
                {mySales.map((sale) => (
                  <tr key={sale._id} className="border-b border-white/5">
                    <td className="py-2">
                      {sale.lead ? `${sale.lead.firstName || ''} ${sale.lead.lastName || ''}`.trim() || '—' : '—'}
                    </td>
                    <td className="py-2">{sale.carrier}</td>
                    <td className="py-2 text-white/70">
                      {PRODUCT_TYPES.find((t) => t.v === sale.productType)?.label || sale.productType}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      ${(sale.annualPremium || 0).toLocaleString()}
                    </td>
                    <td className="py-2 font-mono text-xs text-white/70">{sale.policyNumber}</td>
                    <td className="py-2 text-white/60 text-xs">
                      {sale.applicationDate ? new Date(sale.applicationDate).toLocaleDateString() : '—'}
                    </td>
                    <td className="py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-sm ${sale.status === 'cancelled' ? 'bg-red-500/20 text-red-300' : 'bg-green-500/20 text-green-300'}`}>
                        {sale.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Manager — All Orders + Stats */}
      {isManager && (
        <>
          <div className="panel p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm text-white/70">
              <TrendingUp className="w-4 h-4" /> Spend Share — Weekly Distribution
            </div>
            {stats?.rows?.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-white/50 border-b border-white/10">
                    <tr>
                      <th className="text-left py-2 font-normal">Agent</th>
                      <th className="text-right py-2 font-normal">Weekly $</th>
                      <th className="text-right py-2 font-normal">Share %</th>
                      <th className="text-right py-2 font-normal">Leads This Week</th>
                      <th className="text-right py-2 font-normal">Active Orders</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.rows.map((r) => (
                      <tr key={r.agentId} className="border-b border-white/5">
                        <td className="py-2">{r.name}</td>
                        <td className="py-2 text-right tabular-nums">${r.weeklyAmount.toLocaleString()}</td>
                        <td className="py-2 text-right tabular-nums">{r.sharePct}%</td>
                        <td className="py-2 text-right tabular-nums">{r.leadsReceivedThisWeek}</td>
                        <td className="py-2 text-right tabular-nums">{r.orders}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="text-xs text-white/60">
                      <td className="pt-2 font-medium">Total</td>
                      <td className="pt-2 text-right tabular-nums font-medium">${stats.totalWeekly.toLocaleString()}</td>
                      <td colSpan={3}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="text-sm text-white/50 py-4 text-center">No active orders.</div>
            )}
          </div>

          <div className="panel p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm text-white/70">
              <Users className="w-4 h-4" /> All Orders ({allOrders.length})
            </div>
            <div className="space-y-2">
              {allOrders.map((o) => (
                <OrderRow key={o._id} order={o} showAgent onPause={pauseOrder} onResume={resumeOrder} />
              ))}
            </div>
          </div>

          <div className="panel p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm text-white/70">
              <AlertTriangle className="w-4 h-4 text-yellow-400" /> Manager Pools
            </div>
            <ManagerPools />
          </div>
        </>
      )}
    </div>
  );
}

function OrderRow({ order, showAgent, onPause, onResume, onReorder }) {
  const statusColor = {
    active: 'bg-green-500/20 text-green-300',
    paused: 'bg-yellow-500/20 text-yellow-300',
    completed: 'bg-blue-500/20 text-blue-300',
    cancelled: 'bg-red-500/20 text-red-300',
    aged: 'bg-white/10 text-white/50',
  }[order.status] || 'bg-white/10';

  return (
    <div className="border border-white/5 rounded-md p-3 hover:border-white/15">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs px-2 py-0.5 rounded-sm ${statusColor}`}>{order.status}</span>
            <span className="text-sm font-medium">{LEAD_TYPES.find((t) => t.v === order.leadType)?.label || order.leadType}</span>
            <span className="text-xs text-white/50">${order.weeklyAmount}/wk · ${order.leadCost}/lead</span>
            {showAgent && order.agent && (
              <span className="text-xs text-civix-cyan">→ {order.agent.name || order.agent.email}</span>
            )}
          </div>
          <div className="text-xs text-white/50 mt-1">
            States: {(order.states || []).join(', ') || '—'} · Audience: {order.audiencePreset || 'standard'}
          </div>
          <div className="text-xs text-white/50 mt-1">
            Delivered: {order.leadsReceivedThisWeek || 0} this week ({order.leadsReceivedCount || 0} lifetime) ·
            Remaining budget: ${(order.leadsRemainingBudget || 0).toFixed(2)}
            {order.dailyCap > 0 && ` · Daily cap: ${order.leadsReceivedToday || 0}/${order.dailyCap}`}
          </div>
          <div className="text-xs text-white/40 mt-1">
            Signed by {order.signature?.typedName} on {new Date(order.signature?.ts).toLocaleString()} · clause {order.signature?.clauseVersion}
            {order.reorderedFrom && ' · reorder'}
          </div>
          {order.status === 'paused' && order.pausedReason && (
            <div className="mt-2 text-xs text-yellow-200 flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{order.pausedReason}</span>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          {order.status === 'active' && (
            <button onClick={() => onPause(order._id)} className="text-xs px-2 py-1 rounded-sm border border-white/10 hover:border-white/30 flex items-center gap-1">
              <Pause className="w-3 h-3" /> Pause
            </button>
          )}
          {order.status === 'paused' && (
            <button onClick={() => onResume(order._id)} className="text-xs px-2 py-1 rounded-sm border border-white/10 hover:border-white/30 flex items-center gap-1">
              <Play className="w-3 h-3" /> Resume
            </button>
          )}
          {onReorder && (
            <button
              onClick={() => onReorder(order)}
              className="text-xs px-2 py-1 rounded-sm border border-civix-cyan/40 text-civix-cyan hover:border-civix-cyan flex items-center gap-1"
            >
              <RotateCcw className="w-3 h-3" /> Reorder
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function NewOrderForm({ clauseText, clauseVersion, onDone }) {
  const [form, setForm] = useState({
    leadType: 'final_expense',
    states: [],
    audiencePreset: 'standard',
    weeklyAmount: 500,
    dailyCap: 0,
    leadCost: 25,
    typedName: '',
    accepted: false,
  });
  const [submitting, setSubmitting] = useState(false);

  function toggleState(s) {
    setForm((f) => ({
      ...f,
      states: f.states.includes(s) ? f.states.filter((x) => x !== s) : [...f.states, s],
    }));
  }

  async function submit() {
    if (!form.typedName.trim()) return toast.error('Type your full name to sign');
    if (!form.accepted) return toast.error('You must accept the no-refund/no-cancel clause');
    if (form.states.length === 0) return toast.error('Select at least one state');
    if (form.weeklyAmount < 1) return toast.error('Weekly amount must be at least $1');
    setSubmitting(true);
    try {
      await apiPost('/lead-orders', {
        leadType: form.leadType,
        states: form.states,
        audiencePreset: form.audiencePreset,
        weeklyAmount: Number(form.weeklyAmount),
        dailyCap: Number(form.dailyCap) || 0,
        leadCost: Number(form.leadCost) || 25,
        signature: { typedName: form.typedName.trim(), accepted: true },
      });
      toast.success('Order placed — auto-approved');
      onDone();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="panel p-4 space-y-4">
      <div className="flex items-center gap-2 text-sm text-white/70">
        <FileSignature className="w-4 h-4" /> New Order
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs text-white/60 mb-1">Lead Type</label>
          <select
            value={form.leadType}
            onChange={(e) => setForm({ ...form, leadType: e.target.value })}
            className="input w-full"
          >
            {LEAD_TYPES.map((t) => (
              <option key={t.v} value={t.v}>{t.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-white/60 mb-1">Weekly Amount ($)</label>
          <input
            type="number" min="1"
            value={form.weeklyAmount}
            onChange={(e) => setForm({ ...form, weeklyAmount: e.target.value })}
            className="input w-full"
          />
        </div>
        <div>
          <label className="block text-xs text-white/60 mb-1">Cost / Lead ($)</label>
          <input
            type="number" min="1"
            value={form.leadCost}
            onChange={(e) => setForm({ ...form, leadCost: e.target.value })}
            className="input w-full"
          />
        </div>
        <div>
          <label className="block text-xs text-white/60 mb-1">Audience Preset</label>
          <input
            value={form.audiencePreset}
            onChange={(e) => setForm({ ...form, audiencePreset: e.target.value })}
            className="input w-full"
            placeholder="e.g. age 50-80, homeowner"
          />
        </div>
        <div>
          <label className="block text-xs text-white/60 mb-1">Daily Cap (0 = none)</label>
          <input
            type="number" min="0"
            value={form.dailyCap}
            onChange={(e) => setForm({ ...form, dailyCap: e.target.value })}
            className="input w-full"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs text-white/60 mb-2">
          States ({form.states.length} selected) — must match your licensed states
        </label>
        <div className="flex flex-wrap gap-1">
          {US_STATES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => toggleState(s)}
              className={`text-xs px-2 py-1 rounded-sm border ${form.states.includes(s) ? 'border-civix-purple/50 bg-civix-purple/10' : 'border-white/10 hover:border-white/30'}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Clause */}
      <div className="border border-yellow-500/30 bg-yellow-500/5 rounded-md p-3">
        <div className="flex items-center gap-2 text-xs text-yellow-300 mb-2">
          <AlertTriangle className="w-3.5 h-3.5" /> Required: No-Refund / No-Cancellation Clause (v{clauseVersion})
        </div>
        <div className="max-h-40 overflow-y-auto text-xs text-white/70 whitespace-pre-wrap font-mono">
          {clauseText}
        </div>
        <label className="flex items-start gap-2 mt-3 text-xs">
          <input
            type="checkbox"
            checked={form.accepted}
            onChange={(e) => setForm({ ...form, accepted: e.target.checked })}
            className="mt-0.5"
          />
          <span>I have read and accept the no-refund / no-cancellation clause above.</span>
        </label>
        <div className="mt-2">
          <label className="block text-xs text-white/60 mb-1">Type your full legal name to sign</label>
          <input
            value={form.typedName}
            onChange={(e) => setForm({ ...form, typedName: e.target.value })}
            className="input w-full font-serif italic"
            placeholder="Your full name"
          />
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button onClick={onDone} className="px-3 py-1.5 rounded-md border border-white/10 text-sm hover:border-white/30">
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={submitting || !form.accepted || !form.typedName.trim() || form.states.length === 0}
          className="px-4 py-1.5 rounded-md bg-linear-to-r from-civix-purple to-civix-cyan text-sm font-medium disabled:opacity-50"
        >
          {submitting ? 'Submitting…' : 'Sign & Submit Order'}
        </button>
      </div>
    </div>
  );
}

function ManagerPools() {
  const { data: overflowData } = useQuery({
    queryKey: ['lead-orders-overflow'],
    queryFn: () => apiGet('/lead-orders/pool/overflow'),
  });
  const { data: agedData } = useQuery({
    queryKey: ['lead-orders-aged'],
    queryFn: () => apiGet('/lead-orders/pool/aged'),
  });

  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div>
        <div className="text-xs text-white/50 mb-2">Overflow Pool — leads with no eligible order</div>
        {overflowData?.leads?.length ? (
          <div className="space-y-1 max-h-60 overflow-y-auto">
            {overflowData.leads.map((l) => (
              <div key={l._id} className="text-xs flex justify-between border border-white/5 rounded-sm px-2 py-1">
                <span>{l.firstName} {l.lastName}</span>
                <span className="text-white/50">{l.state} · {l.phone}</span>
              </div>
            ))}
          </div>
        ) : <div className="text-xs text-white/40">Empty</div>}
      </div>
      <div>
        <div className="text-xs text-white/50 mb-2">Aged Leads File — older than 31 days, manager-only</div>
        {agedData?.leads?.length ? (
          <div className="space-y-1 max-h-60 overflow-y-auto">
            {agedData.leads.map((l) => (
              <div key={l._id} className="text-xs flex justify-between border border-white/5 rounded-sm px-2 py-1">
                <span>{l.firstName} {l.lastName}</span>
                <span className="text-white/50">{l.state} · {new Date(l.createdAt).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        ) : <div className="text-xs text-white/40">Empty</div>}
      </div>
    </div>
  );
}

/**
 * SubmitSaleForm — an agent reports business they wrote.
 *
 * This is the single most important form in the distribution loop. Submitting a
 * sale is what stamps resale protection on the lead, so a client who bought a
 * policy can never be recycled into the $5 second-chance pool and sold to
 * another agent.
 */
function SubmitSaleForm({ onDone }) {
  const [form, setForm] = useState({
    leadId: '',
    carrier: '',
    productType: 'final_expense',
    annualPremium: '',
    policyNumber: '',
    applicationDate: new Date().toISOString().slice(0, 10),
    faceAmount: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // The agent's own assigned leads. The API pins assignedAgent for the agent
  // role, so this cannot surface another agent's book.
  const { data: leadsData, isLoading: leadsLoading } = useQuery({
    queryKey: ['leads-assignee-me'],
    queryFn: () => apiGet('/leads?assignee=me&limit=200'),
  });
  const leads = leadsData?.leads || [];

  async function submit() {
    if (!form.leadId) return toast.error('Select the lead this sale belongs to');
    if (!form.carrier.trim()) return toast.error('Carrier is required');
    if (!form.policyNumber.trim()) return toast.error('Policy number is required');
    if (!Number(form.annualPremium)) return toast.error('Annual premium is required');
    if (!form.applicationDate) return toast.error('Application date is required');

    setSubmitting(true);
    try {
      const res = await apiPost('/sales', {
        leadId: form.leadId,
        carrier: form.carrier.trim(),
        productType: form.productType,
        annualPremium: Number(form.annualPremium),
        policyNumber: form.policyNumber.trim(),
        applicationDate: form.applicationDate,
        faceAmount: Number(form.faceAmount) || 0,
      });
      toast.success(res.duplicate ? 'Already recorded — lead stays protected' : 'Sale recorded. Lead is protected from resale.');
      onDone();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="panel p-4 space-y-4">
      <div className="flex items-center gap-2 text-sm text-white/70">
        <BadgeCheck className="w-4 h-4 text-green-400" /> Submit a Sale
      </div>
      <div className="text-xs text-white/50">
        Reporting the sale locks this lead out of the second-chance pool permanently, so nobody else
        can be sold your client.
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <label className="block text-xs text-white/60 mb-1">Lead</label>
          <select
            value={form.leadId}
            onChange={(e) => setForm({ ...form, leadId: e.target.value })}
            className="input w-full"
          >
            <option value="">{leadsLoading ? 'Loading your leads…' : 'Select a lead'}</option>
            {leads.map((l) => (
              <option key={l._id} value={l._id}>
                {`${l.firstName || ''} ${l.lastName || ''}`.trim() || 'Unnamed'} — {l.phone}
                {l.state ? ` (${l.state})` : ''}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs text-white/60 mb-1">Carrier</label>
          <input
            value={form.carrier}
            onChange={(e) => setForm({ ...form, carrier: e.target.value })}
            className="input w-full"
            placeholder="e.g. Mutual of Omaha"
          />
        </div>
        <div>
          <label className="block text-xs text-white/60 mb-1">Product</label>
          <select
            value={form.productType}
            onChange={(e) => setForm({ ...form, productType: e.target.value })}
            className="input w-full"
          >
            {PRODUCT_TYPES.map((t) => (
              <option key={t.v} value={t.v}>{t.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-white/60 mb-1">Annual Premium ($)</label>
          <input
            type="number" min="1" step="0.01"
            value={form.annualPremium}
            onChange={(e) => setForm({ ...form, annualPremium: e.target.value })}
            className="input w-full"
            placeholder="1200"
          />
        </div>
        <div>
          <label className="block text-xs text-white/60 mb-1">Policy Number</label>
          <input
            value={form.policyNumber}
            onChange={(e) => setForm({ ...form, policyNumber: e.target.value })}
            className="input w-full font-mono"
            placeholder="Carrier policy number"
          />
        </div>
        <div>
          <label className="block text-xs text-white/60 mb-1">Application Date</label>
          <input
            type="date"
            value={form.applicationDate}
            onChange={(e) => setForm({ ...form, applicationDate: e.target.value })}
            className="input w-full"
          />
        </div>
        <div>
          <label className="block text-xs text-white/60 mb-1">Face Amount ($, optional)</label>
          <input
            type="number" min="0"
            value={form.faceAmount}
            onChange={(e) => setForm({ ...form, faceAmount: e.target.value })}
            className="input w-full"
            placeholder="25000"
          />
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button onClick={onDone} className="px-3 py-1.5 rounded-md border border-white/10 text-sm hover:border-white/30">
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={submitting}
          className="px-4 py-1.5 rounded-md bg-green-600/80 hover:bg-green-600 text-sm font-medium disabled:opacity-50"
        >
          {submitting ? 'Submitting…' : 'Record Sale'}
        </button>
      </div>
    </div>
  );
}

/**
 * ReorderForm — place another order using a previous order's spec.
 *
 * The spec is copied so the agent does not re-pick states and amounts, but the
 * signature is NOT: the clause has to be signed fresh for every purchase, so the
 * name field starts empty and the checkbox starts unchecked every time.
 */
function ReorderForm({ order, clauseText, clauseVersion, onClose, onDone }) {
  const [weeklyAmount, setWeeklyAmount] = useState(order.weeklyAmount);
  const [typedName, setTypedName] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    if (!typedName.trim()) return toast.error('Type your full name to sign');
    if (!accepted) return toast.error('You must accept the no-refund/no-cancel clause');
    setSubmitting(true);
    try {
      const res = await apiPost(`/lead-orders/${order._id}/reorder`, {
        weeklyAmount: Number(weeklyAmount),
        signature: { typedName: typedName.trim(), accepted: true },
      });
      if (res.underfunded) toast(res.message, { duration: 7000 });
      else toast.success('Reorder placed');
      onDone();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="panel p-4 space-y-4 border border-civix-cyan/30">
      <div className="flex items-center gap-2 text-sm text-white/70">
        <RotateCcw className="w-4 h-4 text-civix-cyan" /> Reorder
      </div>
      <div className="text-xs text-white/60">
        Same spec as your previous order: {LEAD_TYPES.find((t) => t.v === order.leadType)?.label || order.leadType} ·{' '}
        {(order.states || []).join(', ')} · ${order.leadCost}/lead · audience {order.audiencePreset || 'standard'}
      </div>

      <div className="max-w-xs">
        <label className="block text-xs text-white/60 mb-1">Weekly Amount ($)</label>
        <input
          type="number" min="1"
          value={weeklyAmount}
          onChange={(e) => setWeeklyAmount(e.target.value)}
          className="input w-full"
        />
      </div>

      <div className="border border-yellow-500/30 bg-yellow-500/5 rounded-md p-3">
        <div className="flex items-center gap-2 text-xs text-yellow-300 mb-2">
          <AlertTriangle className="w-3.5 h-3.5" /> Sign again for this order (clause v{clauseVersion})
        </div>
        <div className="text-xs text-white/50 mb-2">
          Your earlier signature does not carry over. Each order is signed on its own.
        </div>
        <div className="max-h-32 overflow-y-auto text-xs text-white/70 whitespace-pre-wrap font-mono">
          {clauseText}
        </div>
        <label className="flex items-start gap-2 mt-3 text-xs">
          <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5" />
          <span>I have read and accept the no-refund / no-cancellation clause above.</span>
        </label>
        <div className="mt-2">
          <label className="block text-xs text-white/60 mb-1">Type your full legal name to sign</label>
          <input
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            className="input w-full font-serif italic"
            placeholder="Your full name"
          />
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-3 py-1.5 rounded-md border border-white/10 text-sm hover:border-white/30">
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={submitting || !accepted || !typedName.trim()}
          className="px-4 py-1.5 rounded-md bg-linear-to-r from-civix-purple to-civix-cyan text-sm font-medium disabled:opacity-50"
        >
          {submitting ? 'Submitting…' : 'Sign & Place Reorder'}
        </button>
      </div>
    </div>
  );
}
